import { Button, Group, Modal, SegmentedControl, Select, Stack, Text } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useState, type FC } from 'react';

import { createScheduledAlert, type CreateScheduledAlertInput, type ScheduledAlertFrequency } from '@api/userAlerts';

/**
 * "Schedule this" for any artifact: pick how often and when, and the alert is created with
 * the context the caller hands in. The caller owns what is scheduled (the plan's inputs and
 * its deep link); this dialog owns only the timing.
 */
export interface ScheduleAlertDialogProps {
    opened: boolean;
    onClose: () => void;
    /** Dialog title, e.g. "Schedule: Production to start today". */
    title: string;
    /** One line under the title saying what will run and where the card opens. */
    description?: string;
    /** The alert to create, minus the schedule. */
    alert: Omit<CreateScheduledAlertInput, 'frequency' | 'triggerTime' | 'triggerDayOfWeek'>;
    /** Called with the created alert id. */
    onScheduled?: (alertId: string) => void;
}

const FREQUENCIES: Array<{ value: ScheduledAlertFrequency; label: string }> = [
    { value: 'DAILY', label: 'Daily' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'EVERY_12H', label: 'Every 12h' },
    { value: 'EVERY_6H', label: 'Every 6h' },
];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((label, i) => ({
    value: String(i),
    label,
}));

const ScheduleAlertDialog: FC<ScheduleAlertDialogProps> = ({ opened, onClose, title, description, alert, onScheduled }) => {
    const [frequency, setFrequency] = useState<ScheduledAlertFrequency>('DAILY');
    const [triggerTime, setTriggerTime] = useState('07:00');
    const [dayOfWeek, setDayOfWeek] = useState('1');
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!/^\d{2}:\d{2}$/.test(triggerTime)) {
            notifications.show({ color: 'red', message: 'Pick a time as HH:mm.' });
            return;
        }
        setSaving(true);
        try {
            const created = await createScheduledAlert({
                ...alert,
                frequency,
                triggerTime,
                triggerDayOfWeek: frequency === 'WEEKLY' ? Number(dayOfWeek) : undefined,
            });
            notifications.show({
                color: 'green',
                title: 'Scheduled',
                message: `${alert.alertLabel} runs ${frequency === 'DAILY' ? 'daily' : frequency === 'WEEKLY' ? `every ${DAYS[Number(dayOfWeek)]?.label}` : frequency === 'EVERY_12H' ? 'every 12 hours' : 'every 6 hours'} at ${triggerTime}. It shows on Home after its first run.`,
            });
            onScheduled?.(created.alertId);
            onClose();
        } catch (e) {
            const msg = (e as { error?: { message?: string | string[] }; message?: string })?.error?.message ?? (e as Error)?.message;
            notifications.show({ color: 'red', title: 'Could not schedule', message: Array.isArray(msg) ? msg.join('; ') : msg || 'Unknown error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title={title} centered size="sm">
            <Stack gap="md">
                {description && (
                    <Text size="sm" c="dimmed">
                        {description}
                    </Text>
                )}
                <SegmentedControl
                    size="xs"
                    value={frequency}
                    onChange={(v) => setFrequency(v as ScheduledAlertFrequency)}
                    data={FREQUENCIES}
                />
                {frequency === 'WEEKLY' && (
                    <Select label="Day" size="xs" data={DAYS} value={dayOfWeek} onChange={(v) => setDayOfWeek(v ?? '1')} allowDeselect={false} />
                )}
                <TimeInput
                    label={frequency === 'EVERY_6H' || frequency === 'EVERY_12H' ? 'First run at' : 'Run at'}
                    size="xs"
                    value={triggerTime}
                    onChange={(e) => setTriggerTime(e.currentTarget.value)}
                />
                <Group justify="flex-end" gap="xs">
                    <Button size="xs" variant="default" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button size="xs" onClick={() => void submit()} loading={saving}>
                        Schedule
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default ScheduleAlertDialog;
